import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ChevronDown } from 'lucide-react';

const ButtonSpinner = () => (
  <svg className="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

// Story 18 — split-button dropdown. Deliberately has no isGated/disabled wiring: export must
// stay enabled in every subscription state (READ_ONLY/DORMANT included) for DPDP portability.
function ExportCsvButton({ onExport, exporting }) {
  const { t } = useTranslation('scheduler');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const handlePick = (format, scope = 'week') => {
    setOpen(false);
    onExport(format, scope);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={exporting}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {exporting ? <ButtonSpinner /> : <Download size={15} />}
        {t('export.button')}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-60 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
          <button
            type="button"
            onClick={() => handlePick('grid')}
            className="w-full flex flex-col items-start px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-800">{t('export.gridOption')}</span>
            <span className="text-xs text-gray-400">{t('export.gridOptionHint')}</span>
          </button>
          <button
            type="button"
            onClick={() => handlePick('detailed')}
            className="w-full flex flex-col items-start px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-t border-gray-100"
          >
            <span className="text-sm font-medium text-gray-800">{t('export.detailedOption')}</span>
            <span className="text-xs text-gray-400">{t('export.detailedOptionHint')}</span>
          </button>
          <button
            type="button"
            onClick={() => handlePick('detailed', 'month')}
            className="w-full flex flex-col items-start px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-t border-gray-100"
          >
            <span className="text-sm font-medium text-gray-800">{t('export.monthDetailedOption')}</span>
            <span className="text-xs text-gray-400">{t('export.monthDetailedOptionHint')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ExportCsvButton;
